// src/pages/ResetPassword/ResetPasswordPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaLock,
  FaShieldAlt,
  FaSignInAlt,
  FaTrophy,
} from "react-icons/fa";

import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import {
  checkResetPasswordToken,
  resetPassword,
} from "./../../services/passwordServices";
import backgroundImage from "./HeaderSlider_02.jpg";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

function getPasswordScore(password) {
  let score = 0;

  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  return Math.min(score, 5);
}

function getPasswordLabel(score) {
  if (score <= 1) return "Faible";
  if (score <= 3) return "Correct";
  if (score === 4) return "Bon";
  return "Très bon";
}

export default function ResetPasswordPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const { token } = useParams();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPwd1, setShowPwd1] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [verifying, setVerifying] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(4);

  const passwordScore = useMemo(() => getPasswordScore(password), [password]);

  const mismatch = Boolean(password && confirmPassword && password !== confirmPassword);

  const isFormValid = useMemo(() => {
    return (
      validToken &&
      password.length >= 6 &&
      confirmPassword.length >= 6 &&
      password === confirmPassword
    );
  }, [validToken, password, confirmPassword]);

  useEffect(() => {
    let cancelled = false;

    const verifyToken = async () => {
      if (!token) {
        setValidToken(false);
        setError("Lien de réinitialisation manquant.");
        setVerifying(false);
        return;
      }

      try {
        setVerifying(true);

        const result = await checkResetPasswordToken(token);

        if (cancelled) return;

        setValidToken(Boolean(result?.valid));

        if (!result?.valid) {
          setError(result?.message || "Token de réinitialisation invalide ou expiré.");
        } else {
          setError("");
        }
      } catch (verifyError) {
        console.error("Erreur vérification token :", verifyError);

        if (!cancelled) {
          setValidToken(false);
          setError("Impossible de valider le lien pour le moment.");
        }
      } finally {
        if (!cancelled) {
          setVerifying(false);
        }
      }
    };

    verifyToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!successMessage) return;

    setCountdown(4);

    const intervalId = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          clearInterval(intervalId);
          navigate("/login", {
            replace: true,
            state: {
              passwordReset: true,
            },
          });
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [successMessage, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");

    if (!validToken) {
      setError("Le lien n’est pas valide ou a expiré.");
      return;
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    try {
      setLoading(true);

      const result = await resetPassword(token, password);

      if (result?.success) {
        setSuccessMessage(
          "Votre mot de passe a été réinitialisé avec succès."
        );
        setPassword("");
        setConfirmPassword("");
      } else {
        setError(result?.message || "Impossible de réinitialiser le mot de passe.");
      }
    } catch (resetError) {
      console.error("Erreur réinitialisation :", resetError);
      setError("Impossible de réinitialiser le mot de passe pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-slate-950 bg-cover bg-center px-3 py-8 pt-[120px] text-slate-900 dark:text-white sm:px-5 md:pt-[145px]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <ScrollIndicator />

      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />
      <div className="absolute -right-24 top-20 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
      <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
      <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-[1180px] gap-6 lg:grid-cols-[minmax(0,1fr)_460px] lg:items-center">
        <section className="hidden text-white lg:block">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur">
            <FaTrophy />
            MonAppliTournoi
          </div>

          <h1 className="mt-5 max-w-3xl text-5xl font-bold tracking-tight">
            Définissez un nouveau mot de passe sécurisé.
          </h1>

          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Le lien de réinitialisation est vérifié avant de vous permettre de
            modifier votre mot de passe. Une fois validé, vous pourrez vous
            reconnecter immédiatement.
          </p>

          <div className="mt-8 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            <HeroFeature
              icon={FaShieldAlt}
              title="Lien vérifié"
              text="Contrôle du token avant modification."
            />
            <HeroFeature
              icon={FaKey}
              title="Nouveau mot de passe"
              text="Saisie et confirmation sécurisées."
            />
            <HeroFeature
              icon={FaSignInAlt}
              title="Retour connexion"
              text="Redirection automatique après validation."
            />
          </div>
        </section>

        <section className="mx-auto w-full max-w-xl lg:max-w-none">
          <div className="overflow-hidden rounded-[34px] border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/95">
            <div className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8">
              <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
              <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />

              <div className="relative">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl">
                  <FaKey />
                </div>

                <h2 className="text-3xl font-bold tracking-tight">
                  Réinitialisation
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Choisissez un nouveau mot de passe pour votre compte.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-7">
              {verifying ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-700 dark:border-slate-800 dark:bg-slate-950/60 dark:text-slate-300">
                  Vérification du lien en cours…
                </div>
              ) : null}

              {!verifying && error ? (
                <div
                  className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                  role="alert"
                >
                  <div className="flex items-start gap-3">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                </div>
              ) : null}

              {successMessage ? (
                <div
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300"
                  role="alert"
                >
                  <div className="flex items-start gap-3">
                    <FaCheck className="mt-0.5 shrink-0" />
                    <span>
                      {successMessage} Redirection dans{" "}
                      <strong>{countdown}s</strong>…
                    </span>
                  </div>
                </div>
              ) : null}

              {!verifying && validToken && !successMessage ? (
                <>
                  <Field label="Nouveau mot de passe" icon={FaLock}>
                    <PasswordInput
                      value={password}
                      onChange={(value) => {
                        setPassword(value);
                        setError("");
                      }}
                      show={showPwd1}
                      setShow={setShowPwd1}
                      placeholder="Votre nouveau mot de passe"
                    />

                    <PasswordStrength
                      score={passwordScore}
                      password={password}
                    />
                  </Field>

                  <Field label="Confirmez le mot de passe" icon={FaLock}>
                    <PasswordInput
                      value={confirmPassword}
                      onChange={(value) => {
                        setConfirmPassword(value);
                        setError("");
                      }}
                      show={showPwd2}
                      setShow={setShowPwd2}
                      placeholder="Confirmez le mot de passe"
                      hasError={mismatch}
                    />

                    {mismatch ? (
                      <p className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">
                        Les mots de passe ne correspondent pas.
                      </p>
                    ) : null}
                  </Field>

                  <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-500 px-4 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    <FaKey />
                    {loading
                      ? "Réinitialisation..."
                      : "Réinitialiser le mot de passe"}
                    {!loading ? (
                      <FaArrowRight className="transition group-hover:translate-x-1" />
                    ) : null}
                  </button>
                </>
              ) : null}

              {!verifying && !validToken ? (
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3.5 text-sm font-bold text-white transition hover:bg-indigo-700"
                >
                  <FaEnvelope />
                  Demander un nouveau lien
                </button>
              ) : null}

              <div className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-600 dark:bg-slate-950/60 dark:text-slate-300">
                Retour à la connexion ?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-1 font-bold text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  <FaSignInAlt className="text-xs" />
                  Se connecter
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-indigo-500" />
        {label}
      </span>

      {children}
    </label>
  );
}

function PasswordInput({
  value,
  onChange,
  show,
  setShow,
  placeholder,
  hasError = false,
}) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        className={`${INPUT} pr-12 ${
          hasError
            ? "border-red-400 focus:border-red-400 focus:ring-red-100 dark:border-red-500 dark:focus:ring-red-950"
            : ""
        }`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="new-password"
        required
        placeholder={placeholder}
      />

      <button
        type="button"
        onClick={() => setShow((current) => !current)}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        aria-pressed={show}
        title={show ? "Masquer" : "Afficher"}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
      >
        {show ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  );
}

function PasswordStrength({ score, password }) {
  if (!password) {
    return (
      <p className="mt-2 text-xs font-medium text-slate-400">
        Utilisez au moins 6 caractères. Ajoutez chiffres, majuscules ou symboles
        pour renforcer votre mot de passe.
      </p>
    );
  }

  const label = getPasswordLabel(score);
  const percent = `${(score / 5) * 100}%`;

  const barClass =
    score <= 1
      ? "bg-red-500"
      : score <= 3
      ? "bg-amber-500"
      : "bg-emerald-500";

  return (
    <div className="mt-3">
      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className={`h-full rounded-full transition-all ${barClass}`}
          style={{ width: percent }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="font-semibold text-slate-500 dark:text-slate-400">
          Sécurité du mot de passe
        </span>

        <span className="font-bold text-slate-700 dark:text-slate-200">
          {label}
        </span>
      </div>
    </div>
  );
}

function HeroFeature({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
        <Icon />
      </div>

      <h3 className="font-bold text-white">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}